import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaBullseye,
  FaBook,
  FaRocket,
  FaHeadset,
  FaCheck,
} from "react-icons/fa";
import { submitLead } from "../services/leads";
import { PHONE_PATTERN } from "../constants/lead";
import "./CTA.scss";

const CTA: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    school: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await submitLead(formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Ошибка при отправке формы:", error);
      setErrorMessage(
        "Произошла ошибка :( Можете написать нам в " +
          '<a href="https://instagram.com/beheard.kz" target="_blank" rel="noopener noreferrer">@beheard.kz</a> ' +
          'или в <a href="https://wa.me/77774059845" target="_blank" rel="noopener noreferrer">WhatsApp</a>.',
      );
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsultationRequest = (e: React.FormEvent) => {
    handleSubmit(e);
  };

  return (
    <section id="contact" className="cta section">
      <div className="container">
        <motion.div
          className="cta__content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="cta__text">
            <motion.h2
              className="cta__title"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Начните оптимизацию вашей компании уже сегодня
            </motion.h2>

            <motion.p
              className="cta__subtitle"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Получите персональную консультацию и узнайте, как beheard может
              трансформировать процессы внутри вашего бизнеса
            </motion.p>

            <motion.div
              className="cta__benefits"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="benefit-item">
                <span className="benefit-item__icon">
                  {/* @ts-ignore */}
                  <FaBullseye />
                </span>
                <span>Персональная настройка под ваши потребности</span>
              </div>
              <div className="benefit-item">
                <span className="benefit-item__icon">
                  {/* @ts-ignore */}
                  <FaBook />
                </span>
                <span>Обучение команды включено в стоимость</span>
              </div>
              <div className="benefit-item">
                <span className="benefit-item__icon">
                  {/* @ts-ignore */}
                  <FaRocket />
                </span>
                <span>Быстрый старт за 24 часа</span>
              </div>
              <div className="benefit-item">
                <span className="benefit-item__icon">
                  {/* @ts-ignore */}
                  <FaHeadset />
                </span>
                <span>Круглосуточная поддержка</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="cta__form-container"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="cta__form-card">
              <div className="form-header">
                <h3>Получить консультацию</h3>
                <p>Заполните форму, и мы свяжемся с вами сегодня</p>
              </div>

              {!isSubmitted ? (
                <form
                  onSubmit={handleConsultationRequest}
                  className="contact-form"
                >
                  <div className="form-group">
                    <label htmlFor="name">Имя *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Введите ваше имя"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Телефон *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      pattern={PHONE_PATTERN}
                      title="Например: +7 777 123 45 67"
                      placeholder="+7 (___) ___-__-__"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="school">Название компании *</label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleInputChange}
                      required
                      placeholder="Название вашей организации"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                    />
                  </div>

                  {errorMessage && (
                    <motion.div
                      className="error-message"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      dangerouslySetInnerHTML={{ __html: errorMessage }}
                    />
                  )}

                  <motion.button
                    type="submit"
                    className="btn btn--primary btn--large form-submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="loading-spinner"></span>
                        Отправляем...
                      </>
                    ) : (
                      "Получить консультацию"
                    )}
                  </motion.button>

                  <p className="form-privacy">
                    Нажимая кнопку, вы соглашаетесь с обработкой персональных
                    данных
                  </p>
                </form>
              ) : (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="success-icon">
                    {/* @ts-ignore */}
                    <FaCheck />
                  </div>
                  <h4>Спасибо за заявку!</h4>
                  <p className="error">
                    Мы получили вашу заявку и свяжемся с вами в ближайшее время.
                  </p>
                  <motion.button
                    className="btn btn--secondary"
                    onClick={() => setIsSubmitted(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Отправить ещё одну заявку
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;
